import type { ElementVersionRevisionHistory } from "@v1/types/core-state";
import type {
  ReploElementVersionFilter,
  ReploElementVersionRevision,
} from "schemas/generated/element";
import type { User } from "schemas/generated/user";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import CollapsibleVersionCard from "@v1/components/version-history/CollapsibleVersionCard";
import VersionCard from "@v1/components/version-history/VersionCard";
import {
  selectDraftElementId,
  selectSelectedRevisionId,
  setElementRevisions,
  setSelectedRevisionId,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { elementRouter } from "@v2/trpc/routes/element";
import { ListFilter } from "lucide-react";
import { orderByDate } from "replo-utils/lib/array";
import { ReploElementVersionKinds } from "schemas/element";
import { v4 as uuid } from "uuid";

// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */

const createFakeRevision = (user: User): ReploElementVersionRevision => {
  return {
    id: uuid(),
    createdAt: new Date().toISOString(),
    kind: ReploElementVersionKinds.current,
    title: "Current Version",
    createdBy: user,
  };
};

const AUTOMATIC_VERSIONS = "AUTOMATIC_VERSIONS";

type ElementVersionCombinedList = Array<
  ReploElementVersionRevision | ElementVersionRevisionHistory
>;

const useElementVersionCombinedList = (
  versions: ReploElementVersionRevision[],
  fakeRevision: ReploElementVersionRevision,
) => {
  return React.useMemo(() => {
    const orderedVersions = versions.reduce(
      (
        versionHistory: ElementVersionCombinedList,
        version: ReploElementVersionRevision,
      ) => {
        const mostRecentVersion = versionHistory[versionHistory.length - 1];
        const mostRecentVersionIsAutomatic = Boolean(
          mostRecentVersion?.title == AUTOMATIC_VERSIONS,
        );
        let mostRecentVersionGroup = mostRecentVersionIsAutomatic
          ? (mostRecentVersion as ElementVersionRevisionHistory)
          : null;

        if (version.kind == "automatic") {
          if (!mostRecentVersionIsAutomatic || !mostRecentVersionGroup) {
            mostRecentVersionGroup = {
              title: AUTOMATIC_VERSIONS,
              items: [],
            };
            versionHistory.push(mostRecentVersionGroup);
          }
          mostRecentVersionGroup.items.push(version);
        } else {
          if (
            mostRecentVersionGroup &&
            mostRecentVersionIsAutomatic &&
            mostRecentVersionGroup.items.length < 3
          ) {
            versionHistory = [
              ...versionHistory.slice(0, -1),
              ...mostRecentVersionGroup.items,
            ];
          }
          versionHistory.push(version);
        }
        return versionHistory;
      },
      [],
    );
    return [fakeRevision, ...orderedVersions];
  }, [versions, fakeRevision]);
};

const VERSION_HISTORY_FILTER_OPTIONS: {
  label: string;
  value: string;
}[] = [
  { label: "All", value: "all" },
  { label: "By you", value: "user" },
  { label: "Published", value: "publish" },
  { label: "Restored", value: "revert" },
];

export const VersionHistoryFilter = ({
  filterBy,
  setFilterBy,
}: {
  filterBy: ReploElementVersionFilter;
  setFilterBy: (filter: ReploElementVersionFilter) => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <Combobox.Root
      options={VERSION_HISTORY_FILTER_OPTIONS}
      value={filterBy}
      onChange={(value) => setFilterBy(value as ReploElementVersionFilter)}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Combobox.Trigger>
        <DeprecatedIconButton
          icon={<ListFilter size={12} />}
          tooltipText="Filter"
          variant="secondary"
          size="sm"
          isActive={isOpen}
        />
      </Combobox.Trigger>
      <Combobox.Popover layoutClassName="w-40">
        <Combobox.Content />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default function VersionHistoryPane({
  versions,
  isLoading,
  isFetchingNextPage,
  loadMoreRef,
}: {
  versions: ReploElementVersionRevision[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  loadMoreRef: (node: HTMLElement | null) => void;
}) {
  const dispatch = useEditorDispatch();
  const selectedRevisionId = useEditorSelector(selectSelectedRevisionId);
  const draftElementId = useEditorSelector(selectDraftElementId);
  const { user } = useCurrentUser();

  // TRPC utils for imperative queries (element router)
  const elementUtils = elementRouter.useUtils();

  const orderedVersions: ReploElementVersionRevision[] = orderByDate(
    versions,
    "createdAt",
  );

  const fakeRevision = React.useMemo(() => {
    return createFakeRevision(user!);
  }, [user]);

  const versionHistory: ElementVersionCombinedList =
    useElementVersionCombinedList(orderedVersions, fakeRevision);

  const onItemClick = async (item: ReploElementVersionRevision) => {
    if (item.kind == ReploElementVersionKinds.current) {
      dispatch(setSelectedRevisionId(null));
      return;
    }

    // Set the selected revision ID immediately for UI feedback
    dispatch(setSelectedRevisionId(item.id));

    // Fetch component data for this revision
    if (draftElementId) {
      const response = await elementUtils.getRevisionComponent.fetch({
        elementId: draftElementId,
        revisionId: item.id,
      });

      // Update the revision with the fetched component data
      const updatedVersions = versions.map((version) => {
        if (version.id === item.id) {
          return {
            ...version,
            component: response.component,
          };
        }
        return version;
      });

      // Update Redux state with the revision that now has component data
      dispatch(
        setElementRevisions({
          [draftElementId]: updatedVersions,
        }),
      );
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1 px-2">
      <div className="no-scrollbar mt-1.5 h-full overflow-y-scroll">
        {!isLoading ? (
          <div className="relative">
            {versionHistory.length > 1 && (
              <Separator
                orientation="vertical"
                className={twMerge(
                  "absolute top-4 z-0 ml-[0.65rem]",
                  "max-h-[calc(100%-2rem)]",
                )}
              />
            )}
            {versionHistory.map((version, groupIndex) => {
              const isFirstItem = groupIndex === 0;
              const isLastItem = groupIndex === versionHistory.length - 1;
              if (version.title == AUTOMATIC_VERSIONS) {
                const versionsGroup = version as ElementVersionRevisionHistory;
                return (
                  <div
                    key={versionsGroup.items[0]!.id}
                    ref={isLastItem ? loadMoreRef : undefined}
                  >
                    <CollapsibleVersionCard
                      className={twMerge(
                        isLastItem && "relative bg-white",
                        isFirstItem && "mt-0",
                      )}
                      versionsGroup={versionsGroup}
                      selectedRevisionId={selectedRevisionId}
                      onItemClick={(item) => {
                        void onItemClick(item);
                      }}
                    />
                  </div>
                );
              }
              const item = version as ReploElementVersionRevision;
              const cardIsActive =
                (item.id === fakeRevision.id && !selectedRevisionId) ||
                selectedRevisionId === item.id;
              return (
                <VersionCard
                  key={item.id}
                  ref={isLastItem ? loadMoreRef : undefined}
                  className={twMerge(
                    isLastItem && "relative bg-white",
                    isFirstItem && "mt-0",
                  )}
                  version={item}
                  isActive={cardIsActive}
                  onClick={() => {
                    void onItemClick(item);
                  }}
                />
              );
            })}

            {isFetchingNextPage && (
              <div className="flex min-h-[100px] items-center justify-center">
                <Spinner variant="primary" className="size-[33px]" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center pt-2">
            <Spinner variant="primary" className="size-[33px]" />
          </div>
        )}
      </div>
    </div>
  );
}
